import { Service } from './service.model';
import { OrgModel } from './org.model';

export class Role {
  oid: string;
  name: string;
  displayName: string;
  description: string;
  groupe_etablissement: string;
  adminOrg: string;
  isManager: string;
  canAssign: string;
  assignmentLevel: string;
  parents: Role[];
  services: Service[];
  children: Role[];
}

export class UserRightsHelper {
  private readonly _roles: Role[];
  private readonly _orgs: OrgModel[];

  private _isSupport: boolean | undefined;

  constructor(userRoles: Role[], userOrgs: OrgModel[]) {
    this._roles = userRoles ?? [];
    this._orgs = userOrgs ?? [];
  }

  private isEqualToOrgName(searchvalue: string): boolean {
    return this._orgs.findIndex(r => r.name === searchvalue) > -1;
  }

  private isEqualToRoleNameLowerCase(searchvalue: string): boolean {
    return (
      this._roles.findIndex(r => r.name.toLowerCase() === searchvalue) > -1
    );
  }

  public isSupportICT(): boolean {
    if (this._isSupport) return this._isSupport;

    this._isSupport = this.isEqualToRoleNameLowerCase('supportictvs');
    return this._isSupport;
  }

  public isSupportEDUID(): boolean {
    return this.isEqualToRoleNameLowerCase('supportictvs-eduid');
  }

  public isRefInfo(): boolean {
    const roleIndex = this._roles.findIndex(r =>
      r.name.toLowerCase().includes('ref-info')
    );
    const orgIndex = this._orgs.findIndex(o =>
      o.name.toLowerCase().includes('ref-info')
    );
    return roleIndex > -1 || orgIndex > -1;
  }

  public canAccessToTestFunc(): boolean {
    return this.isEqualToOrgName('vs_DEF_OEN_ENTAppAcces-InfosM365');
  }

  public isDirector(): boolean {
    return (
      this.isEqualToOrgName('vs_Directeur_P') ||
      this.isEqualToOrgName('vs_Directeur_S1') ||
      this.isDirectorS2()
    );
  }

  public isDirectorS2(): boolean {
    return this.isDirectorS2G() || this.isEqualToOrgName('vs_Directeur_S2P');
  }

  public isDirectorS2G(): boolean {
    return this.isEqualToOrgName('vs_Directeur_S2G');
  }

  public isGestionnaireEDUID(): boolean {
    return (
      this.isEqualToOrgName('vs_EDUID-Gestionnaire_P') ||
      this.isEqualToOrgName('vs_EDUID-Gestionnaire_S1') ||
      this.isGestionnaireEDUIDS2()
    );
  }

  public isGestionnaireEDUIDS2(): boolean {
    return this.isGestionnaireEDUIDS2G() || this.isGestionnaireEDUIDS2P();
  }

  public isGestionnaireEDUIDS2P(): boolean {
    return this.isEqualToOrgName('vs_EDUID-Gestionnaire_S2P');
  }

  public isChefDePoleS2P(): boolean {
    return this.isEqualToOrgName('vs_DEF_SFOP_ChefdePole');
  }

  public isGestionnaireEDUIDS2G(): boolean {
    return this.isEqualToOrgName('vs_EDUID-Gestionnaire_S2G');
  }

  public isSecretary(): boolean {
    return (
      this.isEqualToOrgName('vs_SecretaireDirection_P') ||
      this.isEqualToOrgName('vs_SecretaireDirection_S1') ||
      this.isSecretaryS2()
    );
  }

  public isSecretaryS2(): boolean {
    return (
      this.isSecretaryS2G() ||
      this.isEqualToOrgName('vs_SecretaireDirection_S2P')
    );
  }

  public isSecretaryS2G(): boolean {
    return this.isEqualToOrgName('vs_SecretaireDirection_S2G');
  }

  public isGestionnaire(): boolean {
    return (
      this.isEqualToOrgName('vs_IT-Gestionnaire_P') ||
      this.isEqualToOrgName('vs_IT-Gestionnaire_S1') ||
      this.isEqualToOrgName('vs_IT-Gestionnaire_S2G') ||
      this.isEqualToOrgName('vs_IT-Gestionnaire_S2P')
    );
  }

  public isInspecteur(): boolean {
    return (
      this.isEqualToOrgName('vs_Inspecteur_P') ||
      this.isEqualToOrgName('vs_Inspecteur_S1') ||
      this.isEqualToOrgName('vs_Inspecteur_P-S1') ||
      this.isEqualToOrgName('vs_Inspecteur_S2G')
    );
  }

  public isConseillerPedagogique(): boolean {
    return (
      this.isEqualToOrgName('vs_ConseillerPedagogique_P') ||
      this.isEqualToOrgName('vs_ConseillerPedagogique_S1') ||
      this.isEqualToOrgName('vs_ConseillerPedagogique_S2G')
    );
  }

  public isConseilDirection(): boolean {
    return (
      this.isEqualToOrgName('vs_ConseilDirection_P') ||
      this.isEqualToOrgName('vs_ConseilDirection_S1') ||
      this.isEqualToOrgName('vs_ConseilDirection_S2G') ||
      this.isEqualToOrgName('vs_ConseilDirection_S2P')
    );
  }

  public isAdminSE(): boolean {
    return (
      this.isEqualToOrgName('vs_Admin_SE-Direction') ||
      this.isEqualToOrgName('vs_Admin_SE-DirectionElargie')
    );
  }

  public isITPasswordManagerS2P(): boolean {
    return this.isEqualToOrgName('vs_IT-PasswordManager_S2P');
  }

  public isITManagerExtended(): boolean {
    return (
      this.isITManagerS2() ||
      this.isEqualToOrgName('vs_IT-Responsable_S1') ||
      this.isEqualToOrgName('vs_IT-Responsable_P')
    );
  }

  public isITManagerS2(): boolean {
    return this.isITManagerS2G() || this.isITManagerS2P();
  }

  public isITManagerS2P(): boolean {
    return this.isEqualToOrgName('vs_IT-Responsable_S2P');
  }

  public isITManagerS2G(): boolean {
    return this.isEqualToOrgName('vs_IT-Responsable_S2G');
  }

  public isStudentS2(): boolean {
    return this.isStudentS2G() || this.isStudentS2P();
  }

  public isStudentS2G(): boolean {
    return this.isEqualToOrgName('vs_Eleve_S2G');
  }

  public isStudentS2P(): boolean {
    return this.isEqualToOrgName('vs_Eleve_S2P');
  }

  public isTeacherS2P(): boolean {
    return this.isEqualToOrgName('vs_Enseignant_S2P');
  }

  public isTeacherS2G(): boolean {
    return this.isEqualToOrgName('vs_Enseignant_S2G');
  }

  public isTeacherS2(): boolean {
    return this.isTeacherS2G() || this.isTeacherS2P();
  }

  public isStudent(): boolean {
    return this.isEqualToOrgName('vs_Eleve_ALL');
  }

  public isTeacher(): boolean {
    return this.isEqualToOrgName('vs_Enseignant_ALL');
  }

  public canBeChangedAvsInfo(): boolean {
    return this.isTeacherS2P() || this.isStudentS2P();
  }

  public canBeChangedAvsInfoSupport(): boolean {
    return this.isTeacher() || this.isStudentS2P();
  }

  public canChangeAvsInfo(): boolean {
    return this.isGestionnaireEDUIDS2P() || this.isChefDePoleS2P();
  }

  public canManageRoles(): boolean {
    return (
      this.isSupportICT() ||
      this.isDirector() ||
      this.isSecretary() ||
      this.isITManagerS2P()
    );
  }

  canManageUsers(): boolean {
    return (
      this.isSupportICT() ||
      this.isGestionnaireEDUID() ||
      this.isTeacher() ||
      this.isITPasswordManagerS2P() ||
      this.isRefInfo() ||
      this.isDirector() ||
      this.isSecretary() ||
      this.isITManagerExtended()
    );
  }

  public canCreateUsers(): boolean {
    return (
      this.isSupportICT() ||
      this.isDirector() ||
      this.isSecretary() ||
      this.isITManagerS2P()
    );
  }

  public canManagePicturesSelf(): boolean {
    return this.isSupportICT() || this.isStudentS2G();
  }

  public canManagePictureS2G(): boolean {
    return (
      this.isSupportICT() ||
      this.isDirectorS2G() ||
      this.isSecretaryS2G() ||
      this.isITManagerS2G() ||
      this.isGestionnaireEDUIDS2G()
    );
  }

  public canManageExchange(): boolean {
    return (
      this.isSupportICT() ||
      this.isDirector() ||
      this.isSecretary() ||
      this.isITManagerExtended()
    );
  }

  public canViewPictures(): boolean {
    return this.canManagePictureS2G() || this.isTeacherS2G();
  }

  public hasRespInfoRights(): boolean {
    return (
      this.isSupportICT() || this.isITManagerExtended() || this.isRefInfo()
    );
  }

  public hasNewRespInfoRights(): boolean {
    return (
      this.isSupportICT() ||
      this.isDirector() ||
      this.isITManagerExtended() ||
      this.isGestionnaire() ||
      this.isInspecteur() ||
      this.isConseillerPedagogique() ||
      this.isAdminSE() ||
      this.isConseilDirection() ||
      this.isSecretary()
    );
  }
}
